import { useQuery } from '@tanstack/react-query'
import { Alert, Button, DatePicker, Select, Spin, Table } from 'antd'
import type { LabeledValue } from 'antd/lib/select'
import { FilterValue } from 'antd/lib/table/interface'
import moment, { Moment } from 'moment'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { felbesked, useVolym3, utanforBehorighet } from '../../api/volymuppfoljning'
import FlagListHeader from '../../components/header'
import { getAnsvarigPersonId, getAnsvarigPersonName, setAnsvarigPerson } from '../../store/ansvarigPersonSlice'
import { fecthDistribution } from '../arendeFordelning/queries'
import { skapaKolumner, tabellbredd } from './tableConfig'

// Volymuppföljning (pejlen-produkt#107). Hämtar från /volume3. En rad per brukare, beställd och utförd tid
// per månad. Framtida månader går inte att välja (A19). Fel visas som felbesked, inte som en tom tabell (F11).
//
// Listan Byt handläggare är densamma som i Mina ärenden: Ärendefördelningens (A29, ändrad 2026-10-07).
// Den valda handläggaren delas med de andra vyerna och kan komma från annat håll. Den står därför kvar
// och tabellen laddas även när den inte finns i listan. Servern släpper bara igenom handläggare i ens behörighet.

const { RangePicker } = DatePicker

// Förvald period: de fyra senaste månaderna, den pågående inräknad
const forvaldStart = () => moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD')
const forvaltSlut = () => moment().endOf('month').format('YYYY-MM-DD')

const VolumeTable = () => {
  const [start, setStart] = useState<string | null>(forvaldStart)
  const [slut, setSlut] = useState<string | null>(forvaltSlut)
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>({})

  const dispatch = useDispatch()
  const ansvarigPerson = useSelector(getAnsvarigPersonId)
  const ansvarigPersonName = useSelector(getAnsvarigPersonName)

  // Samma fråga och cache som Byt handläggare i Mina ärenden
  const handlaggare = useQuery(['mycases'], fecthDistribution)
  const handlaggarval = useMemo(
    () => (handlaggare.data || []).map((h) => ({ value: h.ansvarig_personnummer, label: h.ansvarig_namn })).sort((a, b) => a.label.localeCompare(b.label)),
    [handlaggare.data]
  )

  const volym = useVolym3(start, slut, ansvarigPerson || null)

  const brukare = useMemo(() => [...(volym.data?.brukare || [])].sort((a, b) => a.namn.localeCompare(b.namn)), [volym.data])
  const kolumner = useMemo(() => (volym.data ? skapaKolumner(volym.data, filters) : []), [volym.data, filters])

  const valjHandlaggare = (val: LabeledValue) => {
    const h = handlaggarval.find((x) => x.value === val.value)
    dispatch(setAnsvarigPerson({ ansvarigPersonId: h ? h.value : '', ansvarigPersonName: h ? h.label : '' }))
  }

  const valjPeriod = (intervall: [Moment | null, Moment | null] | null) => {
    if (!intervall || !intervall[0] || !intervall[1]) {
      setStart(null)
      setSlut(null)
      return
    }
    setStart(intervall[0].clone().startOf('month').format('YYYY-MM-DD'))
    setSlut(intervall[1].clone().endOf('month').format('YYYY-MM-DD'))
  }

  const rensaFilter = () => {
    const tomma = { ...filters }
    Object.keys(tomma).forEach((nyckel) => {
      tomma[nyckel] = null
    })
    setFilters(tomma)
  }

  const innehall = () => {
    if (!ansvarigPerson) return <Alert type="info" message="Välj en handläggare" description="Välj en handläggare i listan Byt handläggare." />
    if (!start || !slut) return <Alert type="info" message="Välj ett datumintervall" description="Information visas först när ett intervall är valt." />
    if (volym.isError && utanforBehorighet(volym.error))
      return <Alert type="info" message="Välj en handläggare" description="Den valda handläggaren ingår inte i de team du har behörighet till. Välj en handläggare i listan Byt handläggare." />
    if (volym.isError) return <Alert type="error" message={felbesked(volym.error)} />
    if (volym.data && brukare.length === 0) return <Alert type="info" message="Inga brukare med hemtjänst under perioden" />
    return (
      <Spin spinning={volym.isFetching}>
        <Table
          className="flag-table"
          columns={kolumner}
          dataSource={brukare}
          rowKey="nyckel"
          bordered
          size="small"
          scroll={{ x: tabellbredd(kolumner), y: 'calc(100vh - 350px)' }}
          pagination={{
            position: ['topRight'],
            pageSize: 10000,
            showTotal: (_, range) => `${range[1]} av ${brukare.length}`,
            className: 'table-pagination-hidden',
            showSizeChanger: false,
          }}
          onChange={(_, nyaFilter) => setFilters(nyaFilter)}
        />
      </Spin>
    )
  }

  return (
    <>
      <FlagListHeader heading={'Volymuppföljning - ' + ansvarigPersonName} title={null} description={null} button={false} />
      <Select
        style={{ minWidth: '20%', marginBottom: '20px', marginRight: '25px' }}
        labelInValue
        value={ansvarigPerson ? { value: ansvarigPerson, label: ansvarigPersonName } : undefined}
        placeholder="Byt handläggare"
        showSearch
        optionFilterProp="label"
        loading={handlaggare.isLoading}
        notFoundContent={handlaggare.isError ? 'Listan kunde inte hämtas' : undefined}
        options={handlaggarval}
        onChange={valjHandlaggare}
      />
      <RangePicker
        className="range-picker"
        style={{ width: 220, marginBottom: '20px', marginRight: '25px' }}
        picker="month"
        format="MMM YYYY"
        placeholder={['Start', 'Slut']}
        value={start && slut ? [moment(start, 'YYYY-MM-DD'), moment(slut, 'YYYY-MM-DD')] : null}
        disabledDate={(manad) => manad.isAfter(moment().endOf('month'))}
        onChange={(intervall) => valjPeriod(intervall as [Moment | null, Moment | null] | null)}
      />
      <Button style={{ marginBottom: '20px', marginRight: '25px' }} onClick={rensaFilter}>
        Rensa filter
      </Button>
      {innehall()}
    </>
  )
}

export default VolumeTable
